// GENERAL
import React from "react";
import { Link } from "react-router-dom";

// UTILS
import { getFontAwesomeIcon } from "../../../../../utils";

// COMPONENTS
import {
	BaseText,
	BaseContainer,
	BaseToolTip,
} from "../../../../../components";
import OrderDetailsContentSection from "../components/OrderDetailsContentSection";

// TYPES
import { ApprovalContentSectionProps } from "../types";

// MODAL SPECIFIC HELPERS
import StatusBadge from "../helpers/badgeColorConversion";
import { convertStatus } from "../helpers/badgeColorConversion";

const ApprovalOrderDetails: React.FC<ApprovalContentSectionProps> = ({
	orderDetails,
	orderSummary,
	orderTotalLease,
	fields,
	aclPermissions,
	orderStatus,
	hostName,
	dateOrder,
	submittedByUsers,
	orderNumber,
	activeUuid,
	approvalDecisions,
	currencyCharacter,
}) => {
	const { convertedStatus, color } = convertStatus(orderStatus, hostName);

	// SETTINGS FROM FIELDS //
	const isEditOnTogaCommerceLinkVisible =
		fields?.orderContentHeader?.editOnTogaCommerce?.isVisible;

	// TEXT FROM FIELDS //
	const orderDetailsHeading =
		fields?.orderContentHeader.orderDetailsHeading?.label;
	const orderedOnLabel = fields?.orderContentHeader.orderDate?.label;
	const submittedByLabel = fields?.orderContentHeader.submittedBy?.label;
	const orderNumberLabel = fields?.orderContentHeader.orderNumber?.label;
	const editOnTogaCommerceLabel =
		fields?.orderContentHeader.editOnTogaCommerce?.label;

	const orderSummaryHeading = fields?.summary?.heading?.label;
	const subTotalLabel = fields?.summary?.subtotal?.label;
	const estTaxLabel = fields?.summary?.estTax?.label;
	const shippingLabel = fields?.summary?.shipping?.label;
	const totalLabel = fields?.summary?.total?.label;

	const recurringChargesHeading = fields?.recurringCharges?.heading?.label;
	const computerSupportLabel =
		fields?.recurringCharges?.computerSupport?.label;
	const totalLeaseLabel = fields?.recurringCharges?.totalLease?.label;

	// CONDITIONALS TO VARS //
	const disabledLink =
		!fields?.orderContentHeader.editOnTogaCommerce?.disableStatusKey?.includes(
			orderStatus,
		) || !aclPermissions.hasEditInCommercePermissions;

	// DATA TO VARS //

	const { tax, total, shipping, subTotal } = orderSummary;

	///// CLASS NAMES ////
	const parentContainerClasses =
		"flex flex-col items-start w-full  max-sm:p-2";
	const orderDetailsHeaderClasses = ` flex sm:flex-row flex-col justify-between sm:items-center w-full `;
	const headingAndStatusContainerClasses = `flex flex-row items-center gap-2 max-sm:flex-col max-sm:items-start`;
	const orderNumberAndLinkClasses = `flex items-center gap-2 font-inter-regular sm:my-0 my-2`;
	const editOrderLinkClasses = `${
		disabledLink
			? "text-navy-300 cursor-not-allowed"
			: "text-hyperlink hover:text-primary-800 hover:underline group"
	} flex items-center gap-1 text-sm   `;

	const getEditOrderDetailsUrl = (env: string) => {
		switch (env) {
			case "development":
				return `http://${hostName}.togacommerce:5173/cart?uuid=${activeUuid}`;
			case "production":
				return `https://${hostName}.togacommerce.com/cart?uuid=${activeUuid}`;
			default:
				return `https://${hostName}.${env}.togacommerce.com/cart?uuid=${activeUuid}`;
		}
		return "#";
	};

	const renderCorrectGridStyles = (): string => {
		const baseClasses = "sm:grid flex-col gap-x-10 gap-y-5";
		const gridCols = "grid-cols-2";
		return `${baseClasses} ${gridCols}`;
	};

	const filteredOrderSummaryFields = fields.orderContent.orderContentDetails;

	return (
		<BaseContainer
			columnContainerClasses={parentContainerClasses}
			columnShape="semi-rounded"
			borderColor="transparent"
			isDisabled={false}
			dataTestId="approval-order-details"
		>
			<div
				className={orderDetailsHeaderClasses}
				data-testid="approval-details-header"
			>
				<div className={headingAndStatusContainerClasses}>
					<BaseText
						size={"text-lg"}
						color={"text-navy-500"}
						text={orderDetailsHeading || "—"}
						fontFamily="font-inter-semibold font-semibold"
						tag={"h1"}
					/>
					<StatusBadge
						statusName={convertedStatus || "—"}
						colorHex={color}
						additionalClasses="flex items-center"
						fontFamily="font-inter-medium font-medium"
					/>
				</div>
				<div className={orderNumberAndLinkClasses}>
					{isEditOnTogaCommerceLinkVisible && (
						<Link
							to={getEditOrderDetailsUrl(import.meta.env.MODE)}
							className={editOrderLinkClasses}
							onClick={(e) => {
								if (disabledLink) {
									e.preventDefault();
								}
							}}
						>
							{!disabledLink && (
								<>
									<span className="group-hover:text-primary-800">
										{editOnTogaCommerceLabel}
									</span>
									<span className="group-hover:text-primary-800">
										{getFontAwesomeIcon(
											"penLine",
											"regular",
										)}
									</span>
								</>
							)}
						</Link>
					)}
				</div>
			</div>

			<div
				className="sm:flex justify-between items-center w-full text-left"
				data-testid="approval-details-date-row"
			>
				<div className="flex">
					<BaseText
						text={`${orderedOnLabel} ${dateOrder}`}
						size="text-sm"
						color="text-gray-900"
						fontFamily="font-inter-regular font-regular"
						tag="p"
						additionalClasses="mt-2"
					/>
					<BaseText
						text={`${submittedByLabel} ${submittedByUsers}`}
						size="text-sm"
						color="text-dark-5"
						fontFamily="font-inter-regular"
						tag="p"
						additionalClasses="mt-2 italic ml-1"
					/>
				</div>
				<BaseText
					text={`${orderNumberLabel} ${orderNumber}`}
					size="text-sm"
					color="text-gray-900"
					fontFamily="font-inter-regular"
					tag="p"
					additionalClasses="mt-2"
				/>
			</div>
			<div className="border-b border-gray-200 w-full mt-3 mb-4"></div>
			<BaseContainer
				columnContainerClasses="md:flex w-full mb-2 max-sm:p-2 "
				columnShape="semi-rounded"
				borderColor="none"
				backgroundColor="white"
				isDisabled={false}
			>
				<div className="w-3/4 text-left">
					<OrderDetailsContentSection
						data={orderDetails}
						sectionContainerClasses="w-full max-xl:w-full"
						detailsContainerClasses={renderCorrectGridStyles()}
						fields={filteredOrderSummaryFields}
						itemContainerClasses=" max-xl:py-3 "
						mobileBreakpoint="flex-col flex"
						approvalDecisions={approvalDecisions}
					/>
				</div>

				<div
					className="w-1/4 text-left max-md:w-1/2 max-md:mt-5 max-sm:w-full"
					data-testid="approval-order-summary"
				>
					<BaseText
						color=""
						text={orderSummaryHeading}
						size="text-sm"
						fontFamily="font-inter-semibold font-semibold"
						tag="h3"
						additionalClasses="mb-2"
					/>
					<div className="space-y-2">
						<div className="flex justify-between items-center">
							<BaseText
								color="text-navy-500"
								text={subTotalLabel}
								size="text-sm"
								fontFamily="font-inter-medium"
								tag="p"
							/>
							<BaseText
								color="text-navy-500"
								text={`${currencyCharacter} ${subTotal}` || "—"}
								size="text-sm"
								fontFamily="font-inter-medium"
								tag="p"
							/>
						</div>
						<div className="flex justify-between items-center">
							<div className="flex items-center gap-2">
								<BaseText
									color="text-navy-500"
									text={estTaxLabel}
									size="text-sm"
									fontFamily="font-inter-medium"
									tag="p"
								/>
								<BaseToolTip
									message="A projected cost based on current data."
									icon="infoCircle"
									iconStyle="solid"
								/>
							</div>
							<BaseText
								color="text-navy-500"
								text={`+ ${currencyCharacter} ${tax}` || "—"}
								size="text-sm"
								fontFamily="font-inter-medium"
								tag="p"
							/>
						</div>
						<div className="flex justify-between items-center">
							<div className="flex items-center gap-2">
								<BaseText
									color="text-navy-500"
									text={shippingLabel}
									size="text-sm"
									fontFamily="font-inter-medium"
									tag="h3"
								/>
								<BaseToolTip
									message="A projected cost based on current data."
									icon="infoCircle"
									iconStyle="solid"
								/>
							</div>
							<BaseText
								color="text-navy-500"
								text={`+ ${currencyCharacter} ${shipping}` || "—"}
								size="text-sm"
								fontFamily="font-inter-medium"
								tag="p"
							/>
						</div>
						<div className="flex justify-between border-b border-gray-200"></div>
						<div
							className="flex justify-between items-center"
							data-testid="approval-order-total"
						>
							<BaseText
								color="text-navy-500"
								text={totalLabel}
								size="text-sm"
								fontFamily="font-inter-semibold font-semibold"
								tag="p"
							/>
							<BaseText
								color="text-navy-500"
								text={`${currencyCharacter} ${total}` || "—"}
								size="text-sm"
								fontFamily="font-inter-semibold font-semibold"
								tag="p"
							/>
						</div>
					</div>
					{orderTotalLease !== "0.00" && (
						<div className="mt-10">
							<BaseText
								color=""
								text={recurringChargesHeading}
								size="text-sm"
								fontFamily="font-inter-semibold font-semibold"
								tag="h3"
								additionalClasses="mb-2"
							/>
							<div className="space-y-2">
								<div className="flex justify-between items-center">
									<BaseText
										color="text-navy-500"
										text={computerSupportLabel}
										size="text-sm"
										fontFamily="font-inter-regular"
										tag="p"
									/>
									<BaseText
										color="text-navy-500"
										text={`${currencyCharacter} ${orderTotalLease}/mo` || "—"}
										size="text-sm"
										fontFamily="font-inter-regular"
										tag="p"
									/>
								</div>

								<div className="flex justify-between border-b border-gray-200"></div>
								<div className="flex justify-between items-center">
									<BaseText
										color="text-navy-500"
										text={totalLeaseLabel}
										size="text-sm"
										fontFamily="font-inter-semibold font-semibold"
										tag="p"
									/>
									<BaseText
										color="text-navy-500"
										text={`${currencyCharacter} ${orderTotalLease}/mo` || "—"}
										size="text-sm"
										fontFamily="font-inter-semibold font-semibold"
										tag="p"
									/>
								</div>
							</div>
						</div>
					)}
				</div>
			</BaseContainer>
		</BaseContainer>
	);
};

export default ApprovalOrderDetails;
